import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IconButton, Tooltip } from '@mui/material';

import { SideMenuItem } from '@/app/ui/SideMenu/config';
import useEllipsisDetection from '@/shared/lib/hooks/useEllipsisDetection';

import {
  ActiveIndicator,
  ButtonIconWrapper,
  ButtonLabel,
  ButtonText,
  SubMenu,
  SubMenuButton,
} from './styled';
import useSideMenuHoverPopover from './useSideMenuHoverPopover';

interface Props {
  submenuItem: SideMenuItem;
  isActiveRoute: (menuItem: SideMenuItem) => boolean;
  isExpanded: boolean;
  isStatistics: boolean;
  collapsed?: boolean;
}

const SideMenuSubitem: FC<Props> = ({
  submenuItem,
  isActiveRoute,
  isExpanded,
  isStatistics,
  collapsed,
}) => {
  const { hidden, label, path, disabled, submenus, icon, tooltip } = submenuItem;

  const { ref, hasEllipsis } = useEllipsisDetection();
  const navigate = useNavigate();
  const { handleMouseEnter, handleMouseLeave, hoverPopover, closePopover } =
    useSideMenuHoverPopover(tooltip ?? label, isExpanded);

  const [collapsedMenus, setCollapsedMenus] = useState<string[]>(
    submenus?.find((s) => isActiveRoute(s)) || collapsed ? [label] : [],
  );

  const isActive = useMemo(
    () => isActiveRoute(submenuItem) && (!submenus?.length || submenus.every((s) => s.hidden)),
    [isActiveRoute, submenuItem, submenus],
  );

  const handleClick = () => {
    closePopover();
    if (path) {
      navigate(path);
    } else if (isStatistics && submenus?.length) {
      setCollapsedMenus((prev) =>
        prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label],
      );
    }
  };

  return !hidden ? (
    <>
      <SubMenuButton
        key={label}
        variant="text"
        disabled={disabled}
        isActive={isActive}
        isExpanded={isExpanded}
        isStatistics={isStatistics && (submenus?.length ?? 0) > 0}
        isStatisticsLastDepth={isStatistics && !submenus?.length}
        data-testid={`submenu-${label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          cursor: (path || submenus?.length) && !disabled ? 'pointer' : 'default',
        }}
      >
        <ButtonLabel isActive={isActive}>
          {isActive && <ActiveIndicator />}

          <Tooltip
            title={hasEllipsis && isExpanded ? (tooltip ?? label) : undefined}
            placement="right"
          >
            <ButtonIconWrapper isExpanded={isExpanded}>
              {icon}

              <ButtonText ref={ref} isActive={isActive} isExpanded={isExpanded}>
                {label}
              </ButtonText>
            </ButtonIconWrapper>
          </Tooltip>

          {!!submenus && isExpanded && isStatistics && (
            <IconButton sx={{ padding: 0 }}>
              {collapsedMenus.includes(label) ? (
                <ChevronDownIcon sx={{ fontSize: '1.25rem' }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
              )}
            </IconButton>
          )}
        </ButtonLabel>
      </SubMenuButton>

      {hoverPopover}

      {collapsedMenus.includes(label) && (
        <SubMenu isExpanded={isExpanded}>
          {submenus?.map((submenu) => (
            <SideMenuSubitem
              key={submenu.label}
              submenuItem={submenu}
              isActiveRoute={isActiveRoute}
              isExpanded={isExpanded}
              isStatistics={isStatistics}
            />
          ))}
        </SubMenu>
      )}
    </>
  ) : null;
};

export default SideMenuSubitem;
